import { useEffect, type ComponentType, type ReactNode } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PATH_FORGOT_PASSWORD, PATH_PRIVACY_POLICY } from "@/routes/paths";
import {
  ArrowDown,
  Building2,
  Camera,
  Clock,
  KeyRound,
  LifeBuoy,
  Link2,
  Mail,
  ShieldCheck,
  Smartphone,
  Users,
} from "lucide-react";

/**
 * Public support page (https://ironpipeline.com/support) — also the App Store
 * Connect "Support URL" for the iOS app.
 *
 * Two audiences land here and the page is split accordingly: people who
 * received an invitation link and just want to take their video call (iPhone
 * app or browser), and the tenants who run their call center in the CRM. The
 * audience picker at the top jumps to the matching section; both sections stay
 * on the page so nothing is hidden behind a tab.
 *
 * Public route (no auth guard), so it carries its own chrome and page
 * background instead of the dashboard layout's.
 */

const SUPPORT_EMAIL = "support@iceberger.ai";
const MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("IronPipeline — support request")}`;

// Keep in sync with the iOS app's deployment target before publishing.
const MIN_IOS_VERSION = "iOS 16";

type QuickFix = {
  icon: ComponentType<{ className?: string }>;
  tile: string;
  iconColor: string;
  title: string;
  body: string;
};

const linkClass =
  "font-medium text-brand-200 underline decoration-brand-200/40 underline-offset-2 transition hover:text-white";

/* ── Audience 1: leads joining a call ─────────────────────────────────────── */

const CALL_QUICK_FIXES: QuickFix[] = [
  {
    icon: Link2,
    tile: "from-brand-500/20 to-brand-300/10",
    iconColor: "text-brand-200",
    title: "I don't have an invitation link",
    body: "You can only join a call with the business that invited you. Look for the email, text message or confirmation page you got after asking to be contacted — the link there takes you to the call. On the iPhone app you can also tap Scan QR code if the business gave you one.",
  },
  {
    icon: Camera,
    tile: "from-accent-purple/20 to-brand-500/10",
    iconColor: "text-brand-200",
    title: "My camera or microphone isn't working",
    body: "Permission is asked the first time you accept a call. In the iPhone app, open Settings, scroll to IronPipeline and turn on Camera and Microphone. In a browser, click the camera icon in the address bar and allow access, then rejoin.",
  },
  {
    icon: Clock,
    tile: "from-accent-amber/20 to-accent-amber/5",
    iconColor: "text-amber-200",
    title: "Nobody has answered yet",
    body: "The standby screen means you're in line and the next available representative will pick up — it isn't an error. Keep the window or app open. If no one answers, open your link again later, or email us.",
  },
];

const CALL_FAQ: { q: string; a: ReactNode }[] = [
  {
    q: "What is IronPipeline?",
    a: (
      <>
        IronPipeline connects you with a live representative by video, right
        after you ask a business to contact you. If you filled out a form,
        tapped an ad or booked a meeting with a company that uses IronPipeline,
        this is where that conversation happens — in your browser, or in the
        IronPipeline app for iPhone.
      </>
    ),
  },
  {
    q: "Do I need to create an account?",
    a: (
      <>
        No. There is no sign-up and no password. You enter your name and email
        so the representative knows who they're talking to, and that's it.
      </>
    ),
  },
  {
    q: "How do I join my call?",
    a: (
      <>
        Open the invitation link you received, enter your name and email, and
        wait on the standby screen. When a representative is ready you'll see an
        incoming call — accept it and you're connected. Your camera and
        microphone only turn on after you accept.
      </>
    ),
  },
  {
    q: "My link opens a web page instead of the iPhone app",
    a: (
      <>
        That happens when the app isn't installed yet, or when the link was
        opened inside another app's built-in browser. Install IronPipeline from
        the App Store, then open the same link again from Mail or Messages. You
        can also take the call in the browser instead — the link works either
        way.
      </>
    ),
  },
  {
    q: "I booked a meeting for a specific time. Where do I find it?",
    a: (
      <>
        Use the link in your confirmation email. It opens your scheduled meeting
        with your details already filled in, so you can join at your appointment
        time.
      </>
    ),
  },
  {
    q: "The video is frozen or the audio keeps cutting out",
    a: (
      <>
        Video calls need a stable connection. Move somewhere with better signal,
        or switch between Wi-Fi and cellular data. Closing other apps or tabs
        that use the camera or stream video also helps. If the connection drops
        completely, you'll see a reconnecting screen and rejoin once you're back
        online.
      </>
    ),
  },
  {
    q: "Can I turn off my camera during the call?",
    a: (
      <>
        Yes. Use the camera and microphone buttons on the call screen to mute
        either one at any time, and the leave button to end the call whenever
        you want.
      </>
    ),
  },
  {
    q: "Which devices are supported?",
    a: (
      <>
        The IronPipeline app runs on iPhone with {MIN_IOS_VERSION} or later. In
        a browser, use a recent version of Chrome, Edge or Safari, on Wi-Fi or
        cellular data.
      </>
    ),
  },
  {
    q: "Is my call recorded?",
    a: (
      <>
        The business you're speaking with may record the conversation for
        quality and training purposes. If it does, you'll be told at the start
        of the call. Questions about a specific recording go to that business —
        or write to us and we'll route it.
      </>
    ),
  },
  {
    q: "What information do you collect, and can you delete it?",
    a: (
      <>
        We collect the name and email you enter so the business you contacted
        can follow up with you, plus the basic technical information needed to
        place the call. To request a copy of your data or have it deleted, email{" "}
        <a href={MAILTO} className={linkClass}>
          {SUPPORT_EMAIL}
        </a>{" "}
        from the address you used, and read our{" "}
        <a href={PATH_PRIVACY_POLICY} className={linkClass}>
          Privacy Policy
        </a>
        .
      </>
    ),
  },
];

/* ── Audience 2: tenants working in the CRM ───────────────────────────────── */

const WORKSPACE_QUICK_FIXES: QuickFix[] = [
  {
    icon: KeyRound,
    tile: "from-brand-500/20 to-brand-300/10",
    iconColor: "text-brand-200",
    title: "I can't sign in",
    body: "Reset your password from the Forgot password link on the sign-in page. If your invitation expired, ask an admin on your team to send it again from Settings → User Management.",
  },
  {
    icon: Users,
    tile: "from-accent-green/20 to-accent-green/5",
    iconColor: "text-accent-green",
    title: "Leads aren't reaching my agents",
    body: "Check that the right call center is selected in the header, that the agent has access to it under Settings → Call Center, and that they're signed in with the Call Center screen open — that's what puts them in the queue.",
  },
  {
    icon: Camera,
    tile: "from-accent-purple/20 to-brand-500/10",
    iconColor: "text-brand-200",
    title: "Camera or mic fails on a call",
    body: "Allow camera and microphone for the site in your browser, use a recent Chrome or Edge, and close any other app holding the camera. Then reload the tab and rejoin the call.",
  },
];

const WORKSPACE_FAQ: { q: string; a: ReactNode }[] = [
  {
    q: "I forgot my password",
    a: (
      <>
        Use the{" "}
        <a href={PATH_FORGOT_PASSWORD} className={linkClass}>
          Forgot password
        </a>{" "}
        link on the sign-in page and follow the email we send you. If the email
        doesn't arrive, check your spam folder before writing to us.
      </>
    ),
  },
  {
    q: "How do I add someone to my team or change their role?",
    a: (
      <>
        Go to Settings → User Management, invite them by email and pick their
        role. Only admins see this screen — if you don't, ask an admin on your
        team.
      </>
    ),
  },
  {
    q: "How do I connect Meta, Google Ads, HubSpot, Google Calendar or Slack?",
    a: (
      <>
        Everything lives under Settings → Integrations, one card per tool. Note
        that Funnels stays empty until Meta or Google Ads is connected — that's
        where its numbers come from.
      </>
    ),
  },
  {
    q: "Where do I find call recordings?",
    a: (
      <>
        Under Call Center → Cloud Recordings, with the session detail one click
        away. Recordings your team makes in personal rooms are listed
        separately.
      </>
    ),
  },
  {
    q: "How do I get the tracking pixel on my website?",
    a: (
      <>
        Settings → Pixel Install has the snippet and the instructions for where
        to paste it. If your site is managed by an agency, forward them that
        page.
      </>
    ),
  },
  {
    q: "Where do I manage my plan and invoices?",
    a: (
      <>
        Settings → Billing. For a change of plan, a refund question or anything
        that page doesn't cover, email us and we'll pick it up.
      </>
    ),
  },
  {
    q: "How do I set the hours I can take meetings?",
    a: (
      <>
        Settings → Availability controls the slots leads can book, and the
        Meeting Scheduler section shows what's already on your calendar. Connect
        Google Calendar under Settings → Integrations to keep both in sync.
      </>
    ),
  },
  {
    q: "How do I report a bug or ask for a feature?",
    a: (
      <>
        Email{" "}
        <a href={MAILTO} className={linkClass}>
          {SUPPORT_EMAIL}
        </a>{" "}
        with what you expected, what happened instead, and the steps to
        reproduce it. A screenshot or short screen recording, plus your browser
        and workspace name, gets it fixed much faster.
      </>
    ),
  },
  {
    q: "Can you export or delete our data?",
    a: (
      <>
        Yes. Write to us from the email address on the account and tell us what
        you need. Our{" "}
        <a href={PATH_PRIVACY_POLICY} className={linkClass}>
          Privacy Policy
        </a>{" "}
        covers how we handle that.
      </>
    ),
  },
];

/* ── Building blocks ──────────────────────────────────────────────────────── */

const QuickFixCard = ({ icon: Icon, tile, iconColor, title, body }: QuickFix) => (
  <article className="glass p-5 transition hover:border-white/[0.14] hover:bg-white/[0.04]">
    <div
      className={`grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br ${tile} ring-1 ring-white/10`}
    >
      <Icon className={`h-[18px] w-[18px] ${iconColor}`} />
    </div>
    <h3 className="mt-4 text-[14px] font-semibold tracking-tight text-white">{title}</h3>
    <p className="mt-2 text-[13px] leading-relaxed text-slate-400">{body}</p>
  </article>
);

const FaqList = ({
  items,
  idPrefix,
}: {
  items: { q: string; a: ReactNode }[];
  idPrefix: string;
}) => (
  <div className="panel mt-4 px-5 sm:px-6">
    <Accordion type="single" collapsible>
      {items.map(({ q, a }, index) => (
        <AccordionItem
          key={q}
          value={`${idPrefix}-${index}`}
          className="border-white/[0.06] last:border-b-0"
        >
          <AccordionTrigger className="gap-4 py-4 text-left text-[14px] font-medium text-white hover:no-underline [&>svg]:text-slate-400">
            {q}
          </AccordionTrigger>
          <AccordionContent className="max-w-[62ch] pb-5 pr-6 text-[13px] leading-relaxed text-slate-400">
            {a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </div>
);

const SectionHeading = ({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
}) => (
  <div className="mb-5">
    <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
      {eyebrow}
    </span>
    <h2 className="mt-1.5 text-[22px] font-semibold tracking-tight text-white">{title}</h2>
    <p className="mt-1 max-w-2xl text-[13px] leading-relaxed text-slate-400">{subtitle}</p>
  </div>
);

const AUDIENCES = [
  {
    icon: Smartphone,
    href: "#joining-a-call",
    title: "I'm joining a video call",
    body: "You got an invitation link from a business and want to talk to them — on your phone or in your browser.",
  },
  {
    icon: Building2,
    href: "#your-workspace",
    title: "I use IronPipeline at work",
    body: "You're an agent, manager or admin running your call center inside IronPipeline.",
  },
];

/* ── Page ─────────────────────────────────────────────────────────────────── */

const SupportContainer = () => {
  useEffect(() => {
    document.title = "Support · IronPipeline";
  }, []);

  // A shared link like /support#your-workspace has to land on that section.
  // The browser can't honour the hash on a client-rendered route (the section
  // doesn't exist yet when it tries, and App scrolls to the top on mount), so
  // the scroll happens here after the first paint.
  useEffect(() => {
    const { hash } = window.location;
    if (!hash) return;

    const target = document.querySelector(hash);
    if (!target) return;

    const raf = requestAnimationFrame(() =>
      target.scrollIntoView({ behavior: "smooth", block: "start" })
    );
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-app-gradient text-white">
      {/* Brand glow behind the hero — same treatment as the redesigned
          dashboards, kept subtle so long-form text stays readable. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[520px] bg-brand-radial" />

      <div className="relative mx-auto w-full max-w-[860px] px-5 pb-20 sm:px-6">
        <header className="flex items-center justify-between gap-4 py-7">
          <img src="/logos/ironpipeline.svg" alt="IronPipeline" className="h-6 w-auto sm:h-7" />
          <a
            href={MAILTO}
            className="inline-flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-2 text-[12.5px] font-medium text-slate-300 transition hover:border-white/[0.14] hover:bg-white/[0.05] hover:text-white"
          >
            <Mail className="h-3.5 w-3.5" />
            Contact us
          </a>
        </header>

        <section className="animate-fade-in-up pt-6 text-center sm:pt-10">
          <span className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
            <LifeBuoy className="h-3 w-3 text-brand-300" />
            IronPipeline Support
          </span>
          <h1 className="grad-text mt-2 text-[32px] font-semibold leading-tight tracking-tight sm:text-[40px]">
            How can we help?
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-[14px] leading-relaxed text-slate-400">
            Answers for taking a video call and for running your call center —
            and a real person to write to if something still isn't working.
          </p>

          <div className="mt-7 flex flex-col items-center gap-3">
            <a
              href={MAILTO}
              className="inline-flex items-center gap-2.5 rounded-xl bg-gradient-to-b from-brand-500 to-brand-600 px-5 py-3 text-[14px] font-semibold text-white shadow-glow transition hover:from-brand-400 hover:to-brand-500"
            >
              <Mail className="h-4 w-4" />
              {SUPPORT_EMAIL}
            </a>
            <p className="text-[12px] text-slate-500">
              We answer every message within one business day.
            </p>
          </div>
        </section>

        {/* Audience picker — jumps to the matching section below. */}
        <nav className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {AUDIENCES.map(({ icon: Icon, href, title, body }) => (
            <a
              key={href}
              href={href}
              className="glass group flex gap-4 p-5 transition hover:border-white/[0.14] hover:bg-white/[0.04]"
            >
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500/20 to-brand-300/10 ring-1 ring-white/10">
                <Icon className="h-[18px] w-[18px] text-brand-200" />
              </div>
              <div className="min-w-0">
                <h2 className="flex items-center gap-1.5 text-[14px] font-semibold tracking-tight text-slate-200 transition group-hover:text-white">
                  {title}
                  <ArrowDown className="h-3.5 w-3.5 text-slate-500 transition group-hover:text-brand-200" />
                </h2>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-400">{body}</p>
              </div>
            </a>
          ))}
        </nav>

        <section id="joining-a-call" className="mt-14 scroll-mt-6">
          <SectionHeading
            eyebrow="Joining a video call"
            title="You were invited to a call"
            subtitle="For anyone who received an invitation link from a business and wants to speak with them, in the IronPipeline app for iPhone or in a browser."
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {CALL_QUICK_FIXES.map((fix) => (
              <QuickFixCard key={fix.title} {...fix} />
            ))}
          </div>
          <FaqList items={CALL_FAQ} idPrefix="call" />
        </section>

        <section id="your-workspace" className="mt-14 scroll-mt-6">
          <SectionHeading
            eyebrow="Your IronPipeline workspace"
            title="You run your call center here"
            subtitle="For agents, managers and admins working in IronPipeline — signing in, getting calls to the right people, and the settings behind it."
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {WORKSPACE_QUICK_FIXES.map((fix) => (
              <QuickFixCard key={fix.title} {...fix} />
            ))}
          </div>
          <FaqList items={WORKSPACE_FAQ} idPrefix="workspace" />
        </section>

        {/* Contact card — `raised` surface with the two decorative glow orbs the
            redesign uses on hero cards. */}
        <section className="relative mt-14 overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-br from-ink-700/60 to-ink-850/80 p-6 shadow-card backdrop-blur-xl sm:p-8">
          <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-brand-500/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-accent-purple/10 blur-3xl" />

          <div className="relative flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="max-w-md">
              <h2 className="text-[18px] font-semibold tracking-tight text-white">
                Still stuck? Write to us
              </h2>
              <p className="mt-2 text-[13px] leading-relaxed text-slate-400">
                Send us an email and we'll get back to you within one business
                day. To speed things up, include:
              </p>
              <ul className="mt-4 space-y-2.5 text-[13px] text-slate-300">
                {[
                  "What you expected, what happened instead, and when",
                  "Your invitation link if you were joining a call, or your workspace name if you work in IronPipeline",
                  "Your device and browser, or your iPhone model and iOS version",
                ].map((text) => (
                  <li key={text} className="flex items-start gap-2.5">
                    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-brand-300" />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <a
              href={MAILTO}
              className="inline-flex shrink-0 items-center gap-2.5 rounded-xl bg-gradient-to-b from-brand-500 to-brand-600 px-5 py-3 text-[14px] font-semibold text-white shadow-glow transition hover:from-brand-400 hover:to-brand-500"
            >
              <Mail className="h-4 w-4" />
              Email support
            </a>
          </div>
        </section>

        <section className="mt-4 flex items-start gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent-green" />
          <p className="text-[12.5px] leading-relaxed text-slate-400">
            We will never ask you for a password, a payment method or any
            financial information — not by email and not during a call. If
            someone claiming to be from IronPipeline does, stop and report it to{" "}
            <a href={MAILTO} className={linkClass}>
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </section>

        <footer className="mt-12 flex flex-col items-center gap-3 border-t border-white/[0.06] pt-7 text-center sm:flex-row sm:justify-between sm:text-left">
          <p className="text-[12px] text-slate-500">
            © {new Date().getFullYear()} Iceberger. All rights reserved.
          </p>
          <div className="flex items-center gap-5 text-[12px]">
            <a href={PATH_PRIVACY_POLICY} className="text-slate-400 transition hover:text-white">
              Privacy Policy
            </a>
            <a href={MAILTO} className="text-slate-400 transition hover:text-white">
              {SUPPORT_EMAIL}
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default SupportContainer;
